/**
 * FERP Portal - Cascade List Page (DRR-125 Sprint 4)
 *
 * Config Type #7 (cs-): Cascade entity mappings — define how Core entity
 * lifecycle events propagate to subgraph domain entities. Loaded by
 * CascadeEmitter at gateway startup (DRR-041/DRR-072).
 *
 * NOTE: No GraphQL list query exists for cascade configs yet. Cascade configs
 * are seeded from configuration and evaluated in-memory by CascadeEmitter.
 * This page shows the known config source and metadata.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays what backend returns
 * - Admin view layer only
 */

import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconBolt, IconGitMerge, IconInfoCircle } from '@tabler/icons-react';

// Known cascade mappings seeded from entity-mappings configuration.
// These are static display rows — a GraphQL list query does not exist for cs- configs yet.
const KNOWN_MAPPINGS = [
  {
    name: 'cs-0-0-1',
    sourceEntity: 'Actor (Human + payroll role)',
    targetSubgraph: 'payroll',
    targetEntity: 'PayrollEmployee',
    action: 'create',
  },
  {
    name: 'cs-0-0-2',
    sourceEntity: 'Actor (Human + healthcare role)',
    targetSubgraph: 'fhir',
    targetEntity: 'FhirPractitioner',
    action: 'create',
  },
  {
    name: 'cs-0-0-3',
    sourceEntity: 'Actor (Human + accounting role)',
    targetSubgraph: 'erp',
    targetEntity: 'ErpPayee',
    action: 'create',
  },
  {
    name: 'cs-0-0-4',
    sourceEntity: 'Actor (Organization)',
    targetSubgraph: 'erp',
    targetEntity: 'ErpVendor',
    action: 'create',
  },
  {
    name: 'cs-0-0-5',
    sourceEntity: 'Actor (Human + healthcare + payroll)',
    targetSubgraph: 'fhir + payroll',
    targetEntity: 'FhirPractitioner + PayrollEmployee',
    action: 'create (dual)',
  },
];

const SUBGRAPH_COLORS: Record<string, string> = {
  payroll: 'teal',
  fhir: 'cyan',
  erp: 'orange',
  'fhir + payroll': 'grape',
};

export function CascadeListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Cascade Configs</Title>
            <Text c="dimmed" size="sm">
              Config type cs- — Core entity lifecycle events propagated to subgraph entities
            </Text>
          </div>
          <Badge variant="light" color="violet" size="lg">
            {KNOWN_MAPPINGS.length} Mappings
          </Badge>
        </Group>

        {/* Info banner */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="No Live GraphQL Query">
          Cascade configs are loaded at gateway startup and evaluated in-memory by
          CascadeEmitter. A backend list resolver for cs- config records is not yet implemented. The
          table below reflects the current seeded entity mappings configuration.
        </Alert>

        {/* Architecture card */}
        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="violet">
              <IconBolt size={18} />
            </ThemeIcon>
            <Text fw={600}>How Cascades Work</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="xs">
            Flow (single code path via DRR-072):
          </Text>
          <Stack gap={4}>
            <Text size="sm">
              Core mutation (Actor create/update) →{' '}
              <Text span ff="monospace" size="sm">
                CascadeEmitter.emit_actor_cascades()
              </Text>
              → match entity mappings →{' '}
              <Text span ff="monospace" size="sm">
                OutboxWriter
              </Text>{' '}
              (ferp-primitives OUTBOX# SK) →{' '}
              <Text span ff="monospace" size="sm">
                OutboxProcessor
              </Text>{' '}
              polls →{' '}
              <Text span ff="monospace" size="sm">
                EventSink
              </Text>{' '}
              (Medplum / PAYROLL / OFBiz)
            </Text>
          </Stack>
        </Card>

        {/* Mappings */}
        <Stack gap="sm">
          {KNOWN_MAPPINGS.map((mapping) => (
            <Card key={mapping.name} withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <ThemeIcon variant="light" color="violet" size="sm">
                    <IconGitMerge size={14} />
                  </ThemeIcon>
                  <Text fw={500} ff="monospace" size="sm">
                    {mapping.name}
                  </Text>
                </Group>
                <Badge
                  variant="light"
                  color={SUBGRAPH_COLORS[mapping.targetSubgraph] ?? 'gray'}
                  size="sm"
                >
                  {mapping.targetSubgraph}
                </Badge>
              </Group>
              <Group gap="xl">
                <div>
                  <Text size="xs" c="dimmed">
                    Source
                  </Text>
                  <Text size="sm">{mapping.sourceEntity}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Target Entity
                  </Text>
                  <Text size="sm" fw={500}>
                    {mapping.targetEntity}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Action
                  </Text>
                  <Badge variant="outline" size="sm">
                    {mapping.action}
                  </Badge>
                </div>
              </Group>
            </Card>
          ))}
        </Stack>

        <Text size="xs" c="dimmed">
          To add or modify cascade mappings, edit{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/cascade/entity-mappings config
          </Anchor>{' '}
          and re-seed (Rust ferp_seed binary).
        </Text>
      </Stack>
    </Container>
  );
}

export default CascadeListPage;
